/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */
import { getGenderFilter } from '../../api/request'

const liveSearchProductsFilter = {
    /**
     * Create filter for live search query
     *
     * This method generates a filter array to be used in live search queries,
     * based on the current view mode (e.g., "catalog") and any selected facets.
     * It supports combining different types of filter conditions depending on the
     * input context and user-selected options.
     *
     * @param {string} type - The type of search to perform, used when the current view is not "catalog".
     * @returns {Array<Object>} - Returns an array of filter objects. Each object contains
     *                            filter conditions in the format:
     *                            - { attribute: string, eq: string } for single-value filters
     *                            - { attribute: string, in: string[] } for multi-value filters
     *
     * Filtering logic:
     * 1. Determine base filter depending on the current view ("catalog" or other).
     * 2. If facets have been selected:
     *    - Group filters by attribute.
     *    - Combine multiple values for the same attribute using an "in" clause.
     *    - For attributes related to "overlayer", apply a fixed filter with value "yes".
     *    - Merge all constructed facet filters into the base filter array.
     *
     * Example return value:
     * [
     *   { attribute: "color", in: ["red", "blue"] },
     *   { attribute: "size", eq: "medium" },
     *   { attribute: "overlayer_active", eq: "yes" }
     * ]
     */
    createFilter(type, genderKey = null) {
        // Base filter criteria
        let filter = this.view === 'catalog'
            ? this.filterCatalog(genderKey)
            : this.filterSearch(type, genderKey)

        // Add selected facets to the filter
        if (this.selectedFacets.length) {
            const facetMap = {}
            this.selectedFacets.forEach(facet => {
                if (facetMap[facet.attribute]) {
                    // Add it to an array if the attribute already exists
                    if (!Array.isArray(facetMap[facet.attribute].in)) {
                        facetMap[facet.attribute].in = [facetMap[facet.attribute].eq]
                        delete facetMap[facet.attribute].eq
                    }
                    facetMap[facet.attribute].in.push(facet.option)
                } else {
                    if (facet.attribute.includes('overlayer')) {
                        facetMap[facet.attribute] = { attribute: facet.attribute, eq: 'yes' }
                    } else {
                        facetMap[facet.attribute] = { attribute: facet.attribute, eq: facet.option }
                    }
                }
            })
            filter = filter.concat(Object.values(facetMap))
        }

        return filter
    },
    /**
     * Filter configuration for catalog view
     *
     * @returns {[{attribute: string, in: string[]},{attribute: string, eq: (string|*)},{attribute: string, eq: string}]}
     */
    filterCatalog(genderKey = null) {
        return [
            { attribute: 'visibility', in: ['Catalog', 'Catalog, Search'] },
            { attribute: 'categoryPath', eq: this.currentCategoryPath },
            { attribute: 'inStock', eq: 'true' },
            { attribute: 'department', eq: getGenderFilter(null, genderKey) }
        ]
    },
    /**
     * Filter configuration for search view
     *
     * @returns {[{attribute: string, in: string[]},{attribute: string, eq: string}]}
     */
    filterSearch(type, genderKey = null) {
        return [
            { attribute: 'visibility', in: ['Catalog, Search'] },
            { attribute: 'inStock', eq: 'true' },
            { attribute: 'department', eq: getGenderFilter(type, genderKey) }
        ]
    }
}

export default liveSearchProductsFilter
